export const WorkItemStatuses = {
  New: 'New',
  InProgress: 'In Progress',
  Completed: 'Completed',
} as const

export const workItemStatusFilterOptions = Object.values(WorkItemStatuses).map((status) => ({
  value: status,
  label: status,
}))

export function normalizeWorkItemStatus(status: string): string | null {
  const trimmed = status.trim().toLowerCase()
  return Object.values(WorkItemStatuses).find((known) => known.toLowerCase() === trimmed) ?? null
}

export function getWorkItemStatusTransitions(status: string): string[] {
  const current = normalizeWorkItemStatus(status)
  if (current === null) return []

  return Object.values(WorkItemStatuses).filter((target) => target !== current)
}

export type ExternalSource = 'AzureDevOps'

export interface WorkItem {
  id: string
  number: number | null
  title: string
  description: string
  epic: string | null
  feature: string | null
  technicalApproach: string | null
  createdAt: string
  status: string
  externalSource: ExternalSource | null
  externalId: string | null
  externalUrl: string | null
  assignedToUserId: string | null
  assignedToDisplayName: string | null
  assignedToEmail: string | null
}

export interface ImportAzureDevOpsWorkItemRow {
  azureDevOpsId: number
  title: string
  description: string | null
  epic: string | null
  feature: string | null
}

export interface ImportAzureDevOpsWorkItemsRequest {
  rows: ImportAzureDevOpsWorkItemRow[]
}

export interface ImportAzureDevOpsWorkItemFailure {
  azureDevOpsId: number | null
  title: string
  errorMessage: string
}

export interface ImportAzureDevOpsWorkItemsResponse {
  imported: number
  skipped: number
  workItemIds: string[]
  failed: ImportAzureDevOpsWorkItemFailure[]
  urlResolutionWarning: string | null
}

export interface AzureDevOpsProcessConfig {
  defaultRequirementType: string
  requirementTypes: string[]
  epicTypes: string[]
  featureTypes: string[]
}

export interface AzureDevOpsWorkItemRef {
  id: number
  title: string
}

export interface PushWorkItemsRequest {
  workItemIds: string[]
  destinationProject: string
  parentWorkItemId?: number
}

export interface PushWorkItemSuccess {
  workItemId: string
  externalId: string
}

export interface PushWorkItemFailure {
  workItemId: string
  errorMessage: string
}

export interface PushWorkItemsResponse {
  pushed: PushWorkItemSuccess[]
  failed: PushWorkItemFailure[]
}

export interface CreateWorkItemRequest {
  title: string
  description: string
  epic?: string
  feature?: string
  technicalApproach?: string
  assignedToUserId?: string | null
}

export interface CreateWorkItemResponse {
  workItemId: string
}

export interface UpdateWorkItemStatusRequest {
  status: string
  expectedStatus: string
}

export interface UpdateWorkItemStatusResponse {
  workItemId: string
  status: string
}

export interface BulkWorkItemStatusTarget {
  workItemId: string
  expectedStatus: string
}

export interface BulkUpdateWorkItemStatusRequest {
  status: string
  items: BulkWorkItemStatusTarget[]
}

export type BulkWorkItemStatusFailureReason = 'Conflict' | 'NotFound'

export interface BulkWorkItemStatusFailure {
  workItemId: string
  reason: BulkWorkItemStatusFailureReason
  currentStatus: string | null
  errorMessage: string
}

export interface BulkUpdateWorkItemStatusResponse {
  status: string
  updatedIds: string[]
  failed: BulkWorkItemStatusFailure[]
}

export interface UpdateWorkItemRequest {
  title: string
  description: string
  epic?: string
  feature?: string
  technicalApproach?: string
  assignedToUserId: string | null
}

export interface AssignWorkItemRequest {
  assignedToUserId: string | null
}

export interface AssignWorkItemResponse {
  workItemId: string
  assignedToUserId: string | null
}

export interface WorkItemStats {
  totalWorkItems: number
  hasSyncedWorkItems: boolean
}

export interface WorkItemTranslation {
  languageCode: string
  translatedTitle: string
  translatedDescription: string
}

export interface WorkItemFilters {
  search?: string
  epics?: string[]
  noEpic?: boolean
  statuses?: string[]
  synced?: boolean
  assignedTo?: string[]
  unassigned?: boolean
  sortBy?: WorkItemSortColumn
  sortDirection?: 'asc' | 'desc'
}

export const UNASSIGNED_FILTER_VALUE = 'unassigned'

export const NO_EPIC_FILTER_VALUE = '__no_epic__'

export type WorkItemSortColumn = 'number' | 'title' | 'externalId' | 'epic' | 'feature' | 'status' | 'created'

export const WORK_ITEM_DEFAULT_SORT_DIRECTIONS: Record<WorkItemSortColumn, 'asc' | 'desc'> = {
  number: 'desc',
  created: 'desc',
  title: 'asc',
  externalId: 'asc',
  epic: 'asc',
  feature: 'asc',
  status: 'asc',
}
